import "./OpenAccountSystemPromptComponent.scss";
import template from "./OpenAccountSystemPromptComponent.html";
import {IComponentOptions, IOnDestroy} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {IvAccountOpeningService} from "../../../../../account-opening/iv/service/IvAccountOpeningService";
import {CurrentUserService} from "../../../../../services/user/CurrentUserService";
import {Branding, getAccountOpeningPath, showAccountOpeningDialog} from "../../../../../types/Branding";
import {ClientEventService} from "../../../../../services/client-events/ClientEventService";
import {UserConfig} from "../../../../../types/UserConfig";
import {BrokerKey} from "../../../../../types/BrokerKey";
import {BackOffice} from "../../../../../types/BackOffice";

export const OpenAccountPrompt = "tfOpenAccountPrompt";

export function openAccountPrompt(): IComponentOptions {
    return {
        template,
        controllerAs: "ctrl",
        controller: OpenAccountSystemPromptComponentController
    };
}

export enum OpenAccountType {
    LINK = "LINK",
    EXTERNAL = "EXTERNAL",
    INTERNAL = "INTERNAL",
}

export class OpenAccountSystemPromptComponentController implements IOnDestroy {

    public readonly accountOpeningPath: Observable<string>;
    public readonly selectedAccountOpeningType: Observable<OpenAccountType>;
    private readonly openDialog$ = new ReplaySubject<boolean>(1);
    private readonly destroy$ = new ReplaySubject<boolean>(1);

    private readonly isLivePrices: boolean;
    private readonly enableOpenAccountMainSection: boolean;
    private readonly enableDelayedDataSection: boolean;
    private readonly disableDelayedDataBanner: boolean;
    private readonly enableOpenAccountMainSectionBranding: Branding[] = [Branding.ALLFIN, Branding.IV, Branding.TF];
    private readonly delayedDataBannerExcludedBrands: BackOffice[] = [ BackOffice.PHILLIP ];
    private readonly enableDelayedDataBranding: Branding[] = [Branding.OPEN_TRADER, Branding.ALLFIN, Branding.IV, Branding.TF];
    /** @ngInject */
    public constructor(private readonly ivAccountOpeningService: IvAccountOpeningService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly clientEventService: ClientEventService,
                       private readonly userConfig: UserConfig) {
        this.isLivePrices = userConfig.livePrices;
        this.enableOpenAccountMainSection = this.enableOpenAccountMainSectionBranding.includes(userConfig.branding);
        this.enableDelayedDataSection = this.enableDelayedDataBranding.includes(userConfig.branding);
        this.disableDelayedDataBanner = this.delayedDataBannerExcludedBrands.includes(userConfig.backOffice);
        this.accountOpeningPath = this.buildAccountOpeningPath();
        this.selectedAccountOpeningType = this.buildAccountOpeningType();
        this.watchOpenDialog();
    }

    private buildAccountOpeningPath(): Observable<string> {
        return this.currentUserService
                   .current
                   .takeUntil(this.destroy$)
                   .map((u) => getAccountOpeningPath(u.branding(), u));
    }

    private buildAccountOpeningType(): Observable<OpenAccountType> {
        return this.currentUserService.current.map((user) => this.accountOpeningType(user.branding()))
                     .publishReplay(1)
                     .refCount();
    }

    private watchOpenDialog() {
        this.openDialog$
            .withLatestFrom(this.currentUserService.current)
            .map(([_, user]) => user)
            .takeUntil(this.destroy$)
            .subscribe((user) => {
                switch (user.branding()) {
                    case Branding.OPEN_TRADER:
                        window.open(getAccountOpeningPath(user.branding(), user) , "_blank");
                        break;
                    case Branding.IV:
                        this.ivAccountOpeningService.start();
                        break;
                }
            });
    }

    public openAccount() {
        this.openDialog$.next(true);
    }

    private accountOpeningType(branding: Branding): OpenAccountType {
        if (!showAccountOpeningDialog(branding)) {
            return OpenAccountType.LINK;
        } else if (branding === Branding.IV) {
            return OpenAccountType.INTERNAL;
        } else {
            return OpenAccountType.EXTERNAL;
        }
    }

    public $onDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

}
